// app/utils/markerIcon.ts
import type { IconBundle } from '../types/resoures'

// ─── Layer 1 — Sync Resolver ───────────────────────────────────────────────

/**
 * Canonical icon state values for resolveIconUrl.
 * Maps to the two keys of IconBundle (online / offline).
 *
 * Callers MUST NOT pass StatusT 'alarm' or 'warning' here.
 * Those states must bypass custom icons entirely (C5 critical state bypass — plan §6).
 */
export type IconState = 'online' | 'offline'

/**
 * Sync resolver — picks the online or offline URL from an IconBundle.
 * Returns the candidate URL string, or null (= no override; caller uses default SVG).
 *
 * Pure function: no I/O, no side-effects, deterministic.
 *
 * C5 critical state bypass: callers must skip this function entirely when
 * StatusT === 'alarm' or StatusT === 'warning' — those states must always
 * use the default alarm/warning SVG (see plan §6, §5.3).
 *
 * @param icon   IconBundle from the camera response, or null/undefined.
 * @param state  'online' | 'offline' (already-normalised; never 'alarm'/'warning').
 * @returns      URL string, or null when there is no custom icon for this state.
 */
export function resolveIconUrl(
  icon: IconBundle | null | undefined,
  state: IconState
): string | null {
  if (!icon) return null
  return icon[state] ?? null
}

// ─── Layer 2 — Async Validator / Probe ────────────────────────────────────

type ProbeState = 'pending' | 'ok' | 'failed'

interface ProbeEntry {
  state: ProbeState
  /** Shared promise for all concurrent callers on the same URL */
  promise: Promise<string | null>
  /** true once console.warn has been emitted for this URL — dedupe guard (C3) */
  warned: boolean
}

/**
 * Module-level cache keyed by URL string.
 * Lives for the page session; no cross-session persistence needed.
 *
 * States (C2):
 *   'pending' — probe in-flight; subsequent calls share the same Promise
 *   'ok'      — probe succeeded; returns url immediately from cache
 *   'failed'  — probe failed; returns null immediately; no re-probe; no duplicate warn (C3)
 */
const probeCache = new Map<string, ProbeEntry>()

/**
 * Async validator — probes a candidate URL via new Image().
 * Deduplicates concurrent and repeated probes per unique URL.
 *
 * Returns the url if the image loads successfully, or null if it fails.
 *
 * Failure behavior (Q9 / C3):
 *   - Emits console.warn('[markerIcon] Failed to load custom icon: <url>') once per unique URL.
 *   - Subsequent calls for the same failed URL return null silently (warned flag).
 *
 * Navigation behavior (C4):
 *   - Cache is retained across re-renders; if a probe was in-flight when the user
 *     navigated away, the next render finds the resolved state ('ok'/'failed') immediately.
 *
 * @param url  Candidate icon URL from resolveIconUrl (non-null guaranteed by caller).
 * @returns    Promise resolving to the url (success) or null (failure).
 */
export function probeIconUrl(url: string): Promise<string | null> {
  const cached = probeCache.get(url)

  if (cached) {
    if (cached.state === 'ok') return Promise.resolve(url)
    if (cached.state === 'failed') return Promise.resolve(null)
    // state === 'pending' — share existing promise; do NOT create a second Image()
    return cached.promise
  }

  // First time seeing this URL — start a new probe
  const promise = new Promise<string | null>((resolve) => {
    const img = new Image()

    img.onload = () => {
      const entry = probeCache.get(url)!
      probeCache.set(url, { state: 'ok', promise, warned: entry.warned })
      resolve(url)
    }

    img.onerror = () => {
      const entry = probeCache.get(url)!
      if (!entry.warned) {
        console.warn(`[markerIcon] Failed to load custom icon: ${url}`)
      }
      probeCache.set(url, { state: 'failed', promise, warned: true })
      resolve(null)
    }

    img.src = url
  })

  probeCache.set(url, { state: 'pending', promise, warned: false })
  return promise
}

// ─── Test-only export ──────────────────────────────────────────────────────

/**
 * @internal — test-only helper; clears the probe cache between test cases.
 * Do NOT call this in production code.
 */
export function _resetProbeCache(): void {
  probeCache.clear()
}
